import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { PageLoader } from '../../components/common/Loader';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useAuth } from '../../contexts/AuthContext';
import { useMyProfile, useEmployees } from '../../hooks/useEmployees';
import { useMonthlyTimesheet } from '../../hooks/useTimesheets';
import {
    useLaborTimesheetCurrent,
    useLaborTimesheetHours,
    useLaborTimesheetDayMeta,
    useLaborTimesheetVariance,
    useLaborTimesheetCumulative,
    useLaborTimesheetSubmit,
    useLaborTimesheetApprove,
    useLaborTimesheetHrApprove,
    useLaborTimesheetReject,
    useDownloadLaborTimesheetReport,
} from '../../hooks/useLaborTimesheets';
import {
    IconChevronLeft, IconChevronRight, IconCalendarMonth, IconChevronDown,
    IconAlertCircle, IconAlertTriangle, IconCheck, IconLock, IconX,
    IconSignature, IconClockHour4,
} from '@tabler/icons-react';
import { months, getAvailableYears } from '../../data/timesheetData';
import ReportDownloadButton from '../../components/common/ReportDownloadButton';
import { isTimesheetHR } from '../../utils/reportPermissions';

const LEAVE_CODES = ['AL', 'SL', 'ML', 'CPD', 'COMP', 'PH'];

const ENGAGEMENT_TYPES = [
    { value: 'type_1', label: 'Type 1', desc: 'HSCL acts as fund manager — charges management fee on disbursed funds', color: 'text-cyan-700 bg-cyan-100' },
    { value: 'type_2', label: 'Type 2', desc: 'HSCL acts as direct implementer — charges direct costs + indirect recovery', color: 'text-green-700 bg-green-100' },
    { value: 'type_3', label: 'Type 3', desc: 'Retainer / advisory — time-based billing', color: 'text-violet-700 bg-violet-100' },
    { value: 'overhead', label: 'Overhead (INDCT)', desc: 'Non-project organisational time — indirect cost pool', color: 'text-indigo-700 bg-indigo-100' },
];

const fmtLoe = (val) => {
    if (val === null || val === undefined) return '-';
    return (parseFloat(val) * 100).toFixed(1) + '%';
};

const fmtHours = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const n = parseFloat(val);
    return isNaN(n) ? '' : n.toFixed(2);
};

const statusColors = {
    draft:       'text-gray-600 bg-gray-100',
    submitted:   'text-white bg-blue-100',
    approved:    'text-indigo-700 bg-indigo-100',
    hr_approved: 'text-green-700 bg-green-100',
    rejected:    'text-red-700 bg-red-100',
    open:        'text-gray-600 bg-gray-100',
};

const loeStatusPill = {
    'On track':     'text-green-700 bg-green-100',
    'Under target': 'text-amber-700 bg-amber-100',
    'Over target':  'text-red-700 bg-red-100',
    '-':            'text-gray-500 bg-gray-100',
};

function loeStatus(contracted, actual) {
    if (contracted === null || contracted === undefined || actual === null || actual === undefined) return '-';
    const diff = (parseFloat(actual) - parseFloat(contracted)) * 100;
    if (Math.abs(diff) <= 5) return 'On track';
    return diff < 0 ? 'Under target' : 'Over target';
}

export default function LaborTimesheet() {
    const { user, hasApiPermission } = useAuth();
    const { data: myProfile } = useMyProfile();

    const now = new Date();
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const monthPickerRef = useRef(null);
    const availableYears = getAvailableYears();

    const { data: fetchedSheet, isLoading, isError } = useLaborTimesheetCurrent(selectedYear, selectedMonth);

    // Fetch standard monthly timesheet in parallel — it carries line_manager which labor endpoint omits
    const { data: monthlyData } = useMonthlyTimesheet(user?.orgId, selectedYear, selectedMonth, user?.employeeId);
    const [sheet, setSheet] = useState(null);

    useEffect(() => {
        if (fetchedSheet) setSheet(fetchedSheet);
    }, [fetchedSheet]);

    const debounceTimers = useRef({});

    const hoursApi      = useLaborTimesheetHours();
    const dayMetaApi    = useLaborTimesheetDayMeta();
    const varianceApi   = useLaborTimesheetVariance();
    const cumulativeApi = useLaborTimesheetCumulative();
    const submitApi     = useLaborTimesheetSubmit();
    const approveApi    = useLaborTimesheetApprove();
    const hrApproveApi  = useLaborTimesheetHrApprove();
    const rejectApi     = useLaborTimesheetReject();
    const downloadLaborReport = useDownloadLaborTimesheetReport();

    const [rejectModal, setRejectModal] = useState({ open: false, reason: '' });
    const [varianceText, setVarianceText] = useState('');
    const varianceTimer = useRef(null);

    useEffect(() => {
        if (sheet?.variance_explanation !== undefined) {
            setVarianceText(sheet.variance_explanation || '');
        }
    }, [sheet?.variance_explanation]);

    useEffect(() => {
        const handler = (e) => {
            if (monthPickerRef.current && !monthPickerRef.current.contains(e.target)) {
                setShowMonthPicker(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ─── Derived flags ──────────────────────────────────────────────────────────

    // HR-level timesheet approval permission (unrelated to report downloads).
    const isHR          = user?.is_superuser || hasApiPermission('rbacapp.ts_act');
    const isOwner       = sheet?.employee === user?.employeeId;
    const isLineManager = monthlyData?.line_manager?.id === user?.employeeId;

    // Report-download capability: only rbacapp.hr_act (or superuser) can pick
    // an employee / download for the whole organization.
    const isHRForReports = isTimesheetHR(user, hasApiPermission);
    const { data: reportEmployees } = useEmployees(user?.orgId, isHRForReports);

    const errorIssues = sheet?.issues?.filter((i) => i.level === 'error') || [];
    const warnIssues  = sheet?.issues?.filter((i) => i.level === 'warning') || [];

    const canSubmit    = isOwner && ['draft', 'open', 'rejected'].includes(sheet?.status);
    const canApprove   = isLineManager && sheet?.status === 'submitted';
    const canHrApprove = isHR && sheet?.status === 'approved';
    const canReject    = (isLineManager && sheet?.status === 'submitted') ||
                         (isHR && ['submitted', 'approved'].includes(sheet?.status));

    // inputs editable when status is draft/open/rejected
    const isLocked = !['draft', 'open', 'rejected'].includes(sheet?.status);

    // ─── Helpers ────────────────────────────────────────────────────────────────

    const mutateSheet = useCallback((data) => setSheet(data), []);

    // entries is a flat array: [{ project_row, day_index, hours }]
    const getEntryHours = useCallback((rowId, dayIndex) => {
        if (!sheet?.entries) return '';
        const entry = sheet.entries.find((e) => e.project_row === rowId && e.day_index === dayIndex);
        return entry ? entry.hours : '';
    }, [sheet]);

    const getLineTotalHours = useCallback((row) => {
        if (!sheet?.entries) return 0;
        return sheet.entries
            .filter((e) => e.project_row === row.id)
            .reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
    }, [sheet]);

    // ─── Cell / day handlers ────────────────────────────────────────────────────

    const handleCellChange = (rowId, dayIndex, value) => {
        const key = `${rowId}-${dayIndex}`;
        clearTimeout(debounceTimers.current[key]);
        debounceTimers.current[key] = setTimeout(() => {
            hoursApi.mutate(
                { id: sheet.id, project_row_id: rowId, day_index: dayIndex, hours: value || '0.00' },
                { onSuccess: mutateSheet }
            );
        }, 500);
    };

    const handleDayMeta = (dayIndex, field, value) => {
        dayMetaApi.mutate(
            { id: sheet.id, day_index: dayIndex, [field]: value },
            { onSuccess: mutateSheet }
        );
    };

    const handleCumulativeBlur = (lineId, value) => {
        if (!value) return;
        const fraction = (parseFloat(value) / 100).toFixed(4);
        cumulativeApi.mutate(
            { id: sheet.id, project_row_id: lineId, cumulative_loe: fraction },
            { onSuccess: mutateSheet }
        );
    };


    const handleVarianceChange = (val) => {
        setVarianceText(val);
        clearTimeout(varianceTimer.current);
        varianceTimer.current = setTimeout(() => {
            varianceApi.mutate(
                { id: sheet.id, variance_explanation: val },
                { onSuccess: mutateSheet }
            );
        }, 800);
    };

    // ─── Approval actions ───────────────────────────────────────────────────────

    const handleSubmit = () => {
        submitApi.mutate({ id: sheet.id }, {
            onSuccess: (data) => { mutateSheet(data); toast.success('Timesheet submitted.'); },
            onError: (err) => {
                const issues = err?.response?.data?.detail;
                if (Array.isArray(issues)) issues.forEach((i) => toast.error(i.message));
                else toast.error(err?.response?.data?.message || 'Submission failed.');
            },
        });
    };

    const handleApprove = () => {
        approveApi.mutate({ id: sheet.id }, {
            onSuccess: (data) => { mutateSheet(data); toast.success('Timesheet approved.'); },
        });
    };

    const handleHrApprove = () => {
        hrApproveApi.mutate({ id: sheet.id }, {
            onSuccess: (data) => { mutateSheet(data); toast.success('HR approval recorded.'); },
        });
    };

    const handleReject = () => {
        if (!rejectModal.reason.trim()) { toast.error('Rejection reason is required.'); return; }
        rejectApi.mutate({ id: sheet.id, reason: rejectModal.reason.trim() }, {
            onSuccess: (data) => {
                mutateSheet(data);
                setRejectModal({ open: false, reason: '' });
                toast.success('Timesheet rejected and returned to staff.');
            },
        });
    };

    // ─── Period navigation ──────────────────────────────────────────────────────

    const goToPrevMonth = () => {
        if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear((y) => y - 1); }
        else setSelectedMonth((m) => m - 1);
    };
    const goToNextMonth = () => {
        if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear((y) => y + 1); }
        else setSelectedMonth((m) => m + 1);
    };

    if (isLoading && !sheet) return <PageLoader />;

    // ─── Derived data ───────────────────────────────────────────────────────────

    const projectRows = sheet?.project_rows || [];
    // filter out the day_index:31 padding row that has is_in_month:false
    const visibleDays = (sheet?.days || []).filter((d) => d.is_in_month && d.work_date);
    const loeSummary  = sheet?.loe_summary || [];

    // backend pre-computes these — use them directly
    const grandTotalHours    = sheet?.month_totals?.grand_total ?? 0;
    const totalContractedLoe = projectRows.reduce((s, r) => s + parseFloat(r.contracted_loe || 0), 0);

    const statusLabel = (sheet?.status || 'draft')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

    // top-level fields (not nested under period)
    const periodStart = sheet?.period_start ?? '';
    const periodEnd   = sheet?.period_end   ?? '';
    const periodLabel = selectedMonth
        ? `${months[selectedMonth - 1]} ${selectedYear}`
        : '';

    return (
        <div className="flex flex-col min-h-screen gap-20 font-sans text-text-primary">
            <Breadcrumb
                title="Labour Distribution Timesheet"
                path="Time & Expense / Time / Timesheets / Labour Distribution"
            />

            {/* ── SECTION A — Staff & Period Information ── */}
            <div className="bg-white rounded border border-border shadow-sm p-20">
                <div className="flex items-center justify-between mb-4">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-org-blue-primary">
                        Section A — Staff &amp; Period Information
                    </p>
                    <div className="flex items-center gap-10">
                        {/* Period navigation */}
                        <button onClick={goToPrevMonth} className="p-6 rounded border border-border hover:bg-org-blue-primary/10 hover:border-org-blue-primary transition-colors">
                            <IconChevronLeft size={16} className="text-org-blue-primary" />
                        </button>
                        <div className="relative" ref={monthPickerRef}>
                            <button
                                onClick={() => setShowMonthPicker(!showMonthPicker)}
                                className="flex items-center gap-8 bg-gradient-to-r from-org-blue-primary/10 to-org-green-primary/10 rounded-lg px-12 py-6 border border-org-blue-primary/20 hover:border-org-blue-primary transition-colors"
                            >
                                <IconCalendarMonth size={14} className="text-org-blue-primary" />
                                <span className="text-sm font-bold text-text-primary">{periodLabel}</span>
                                <IconChevronDown size={12} className={`text-org-blue-primary transition-transform ${showMonthPicker ? 'rotate-180' : ''}`} />
                            </button>
                            {showMonthPicker && (
                                <div className="absolute top-full left-0 mt-4 bg-bg-dark border border-border rounded-lg shadow-lg z-50 p-12 min-w-[220px]">
                                    <div className="mb-10">
                                        <p className="text-[10px] text-text-secondary font-semibold uppercase mb-6">Year</p>
                                        <div className="flex gap-6">
                                            {availableYears.map((year) => (
                                                <button key={year} onClick={() => setSelectedYear(year)}
                                                    className={`flex-1 py-5 rounded text-xs font-semibold transition-colors ${selectedYear === year ? 'bg-org-blue-primary text-white' : 'bg-bg-light text-text-secondary hover:bg-org-blue-primary/10'}`}>
                                                    {year}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-text-secondary font-semibold uppercase mb-6">Month</p>
                                        <div className="grid grid-cols-3 gap-5">
                                            {months.map((m, idx) => (
                                                <button key={m} onClick={() => { setSelectedMonth(idx + 1); setShowMonthPicker(false); }}
                                                    className={`py-5 px-6 rounded text-[11px] font-semibold transition-colors ${selectedMonth === idx + 1 ? 'bg-org-green-primary text-white' : 'bg-bg-light text-text-secondary hover:bg-org-green-primary/10'}`}>
                                                    {m.slice(0, 3)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <button onClick={goToNextMonth} className="p-6 rounded border border-border hover:bg-org-blue-primary/10 hover:border-org-blue-primary transition-colors">
                            <IconChevronRight size={16} className="text-org-blue-primary" />
                        </button>
                        <span className={`inline-flex items-center gap-5 px-10 py-5 rounded-full text-xs font-bold uppercase ${statusColors[sheet?.status] || statusColors.open}`}>
                            {isLocked && <IconLock size={11} />}
                            {statusLabel}
                        </span>
                    </div>
                </div>

                {/* Info grid — matches Excel Section A fields */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-20 gap-y-14 pt-15 mt-12 border-t border-border">
                    {[
                        { label: 'Full Name',        value: sheet?.employee?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username },
                        { label: 'Staff ID',         value: sheet?.employee?.employee_id || monthlyData?.employee?.employee_id || myProfile?.employee_no || '—' },
                        { label: 'Entity',           value: sheet?.entity || myProfile?.entity || '—' },
                        { label: 'Report Period',    value: periodLabel || '—' },
                        { label: 'Department',       value: myProfile?.department_name || myProfile?.department || '—' },
                        { label: 'Grade / Level',    value: myProfile?.grade || myProfile?.grade_level || '—' },
                        { label: 'Line Manager',     value: monthlyData?.line_manager?.name || sheet?.line_manager?.name || myProfile?.supervisor_name || '—' },
                        { label: 'Work Location',    value: myProfile?.location || myProfile?.office || '—' },
                    ].map(({ label, value }) => (
                        <div key={label}>
                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mb-3">{label}</p>
                            <p className="text-sm font-semibold text-gray-800 truncate">{value}</p>
                        </div>
                    ))}
                </div>

                {periodStart && (
                    <p className="mt-12 pt-12 border-t border-border text-xs text-gray-400">
                        Period: <strong className="text-gray-600">{periodStart} → {periodEnd}</strong>
                    </p>
                )}

                <div className="flex items-center justify-end gap-10 pt-15 mt-12 border-t border-border">
                    <ReportDownloadButton
                        label="Download Report"
                        orgId={user?.orgId}
                        canPickEmployee={isHRForReports}
                        employees={reportEmployees || []}
                        currentEmployeeId={user?.employeeId}
                        selfOptionLabel='My Timesheet'
                        isPending={downloadLaborReport.isPending}
                        onDownload={(params) => downloadLaborReport.mutate(params)}
                    />
                </div>

                {sheet?.status === 'rejected' && sheet.rejection_reason && (
                    <div className="mt-15 p-10 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        <strong>Rejected:</strong> {sheet.rejection_reason}
                    </div>
                )}
            </div>

            {/* ── ISSUES PANEL ── */}
            {errorIssues.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-15">
                    <div className="flex items-center gap-8 text-red-700 font-semibold text-sm mb-8">
                        <IconAlertCircle size={18} /> Blocking Issues — resolve before submitting
                    </div>
                    <ul className="text-xs text-red-600 space-y-4">
                        {errorIssues.map((issue, i) => (
                            <li key={i} className="flex items-start gap-6">
                                <span className="w-4 h-4 mt-1 rounded-full bg-red-400 flex-shrink-0" />
                                {issue.message}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {warnIssues.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-15">
                    <div className="flex items-center gap-8 text-amber-700 font-semibold text-sm mb-8">
                        <IconAlertTriangle size={18} /> Warnings
                    </div>
                    <ul className="text-xs text-amber-600 space-y-4">
                        {warnIssues.map((issue, i) => (
                            <li key={i} className="flex items-start gap-6">
                                <span className="w-4 h-4 mt-1 rounded-full bg-amber-400 flex-shrink-0" />
                                {issue.message}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* ── SECTION B — Project & Engagement Register ── */}
            {sheet && (
                <div className="bg-white rounded border border-border shadow-sm overflow-hidden">
                    <div className="px-15 py-10 border-b border-border bg-org-blue-primary">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white">
                            Section B — Project &amp; Engagement Register
                        </p>
                    </div>
                    {projectRows.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="border-collapse text-xs w-full min-w-max">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-border">
                                        <th className="px-10 py-8 text-left text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border">Fund Code</th>
                                        <th className="px-10 py-8 text-left text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border min-w-[200px]">Project / Engagement Name</th>
                                        <th className="px-10 py-8 text-left text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border">Eng. Type</th>
                                        <th className="px-10 py-8 text-left text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border">Entity</th>
                                        <th className="px-10 py-8 text-center text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border">Contracted LOE %</th>
                                        <th className="px-10 py-8 text-left text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border">Contract Period</th>
                                        <th className="px-10 py-8 text-center text-[10px] font-bold uppercase tracking-wide text-gray-500">Col Ref</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projectRows.map((row, i) => (
                                        <tr key={row.id} className={`border-b border-border ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                            <td className="px-10 py-8 border-r border-border">{row.fund_code || row.project?.code || '—'}</td>
                                            <td className="px-10 py-8 border-r border-border font-medium">{row.engagement_name || row.project?.name || '—'}</td>
                                            <td className="px-10 py-8 border-r border-border">
                                                {(() => {
                                                    const et = ENGAGEMENT_TYPES.find((t) => t.value === (row.engagement_type || row.type));
                                                    if (!et) return row.engagement_type || row.type || '—';
                                                    return (
                                                        <span className={`inline-block px-8 py-2 rounded-full text-[10px] font-semibold whitespace-nowrap ${et.color}`}>
                                                            {et.label}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-10 py-8 border-r border-border">{row.entity || '—'}</td>
                                            <td className="px-10 py-8 text-center border-r border-border font-semibold">{fmtLoe(row.contracted_loe)}</td>
                                            <td className="px-10 py-8 border-r border-border">{row.contract_period || '—'}</td>
                                            <td className="px-10 py-8 text-center font-bold text-org-blue-primary">{row.col_ref || row.activity_code || `P${i + 1}`}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-org-blue-primary/5 border-t-2 border-org-blue-primary">
                                        <td colSpan={4} className="px-10 py-8 text-right text-[10px] font-bold uppercase tracking-wide text-gray-600">Total Contracted LOE</td>
                                        <td className={`px-10 py-8 text-center font-bold ${Math.abs(totalContractedLoe - 1) > 0.001 ? 'text-red-600' : 'text-green-700'}`}>
                                            {fmtLoe(totalContractedLoe)}
                                        </td>
                                        <td colSpan={2} />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-20 text-sm text-gray-400 text-center">
                            No project lines registered yet. Contact Finance to set up your project assignments.
                        </div>
                    )}
                    {Math.abs(totalContractedLoe - 1) > 0.001 && projectRows.length > 0 && (
                        <div className="px-15 py-10 bg-red-50 border-t border-red-200 flex items-center gap-8 text-xs text-red-700">
                            <IconAlertTriangle size={14} />
                            Contracted LOE totals {fmtLoe(totalContractedLoe)} — must equal 100% before submission.
                        </div>
                    )}
                    <div className="px-15 py-10 border-t border-border bg-gray-50/50">
                        <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-6">Engagement Type Legend</p>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-20 gap-y-6">
                            {ENGAGEMENT_TYPES.map((t) => (
                                <div key={t.value} className="flex items-center gap-8 text-xs">
                                    <dt className="flex-shrink-0">
                                        <span className={`inline-block px-8 py-2 rounded-full text-[10px] font-semibold whitespace-nowrap ${t.color}`}>
                                            {t.label}
                                        </span>
                                    </dt>
                                    <dd className="text-gray-500">{t.desc}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            )}

            {/* ── SECTION C — Daily Labour Distribution Grid ── */}
            {sheet && (
                <div className="bg-white rounded border border-border shadow-sm overflow-hidden">
                    <div className="px-15 py-10 border-b border-border bg-org-blue-primary flex items-center justify-between">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white">
                            Section C — Daily Labour Distribution
                        </p>
                        <span className="text-[10px] text-white/70">{projectRows.length} project column(s)</span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="border-collapse text-xs min-w-max w-full">
                            <thead>
                                {/* Column headers — order matches Excel: # | Day | [projects] | Leave | PH | Total | Note */}
                                <tr className="bg-org-blue-primary text-white">
                                    <th className="sticky left-0 z-20 bg-org-blue-primary px-6 py-6 text-center border-r border-white/20 min-w-[36px]">#</th>
                                    <th className="sticky left-[36px] z-20 bg-org-blue-primary px-8 py-6 text-left border-r border-white/20 min-w-[90px]">Day</th>
                                    {projectRows.map((row) => (
                                        <th key={row.id}
                                            className={`px-6 py-6 text-center border-r border-white/20 min-w-[80px] ${row.is_indirect ? 'bg-indigo-800' : ''}`}>
                                            <div className="font-bold">{row.col_ref || row.activity_code || '—'}</div>
                                            <div className="text-[9px] opacity-75 font-normal truncate max-w-[70px] mx-auto">{row.engagement_name || row.project?.name}</div>
                                            <div className="text-[9px] opacity-55 font-normal">(hrs)</div>
                                        </th>
                                    ))}
                                    <th className="px-6 py-6 text-center border-r border-white/20 min-w-[70px]">Leave<br />Code</th>
                                    <th className="px-6 py-6 text-center border-r border-white/20 min-w-[40px]">PH</th>
                                    <th className="sticky right-[100px] z-20 bg-org-blue-primary px-6 py-6 text-center border-l border-white/20 min-w-[60px]">Total<br />Hrs</th>
                                    <th className="sticky right-0 z-20 bg-org-blue-primary px-8 py-6 text-left border-l border-white/20 min-w-[100px]">Activity Note</th>
                                </tr>

                                {/* Contracted LOE row */}
                                <tr className="bg-gray-50 border-b border-border text-gray-600">
                                    <td className="sticky left-0 z-10 bg-gray-50 px-6 py-4 border-r border-border text-center text-gray-400 font-bold" />
                                    <td className="sticky left-[36px] z-10 bg-gray-50 px-8 py-4 border-r border-border">
                                        <span className="font-semibold text-[10px] uppercase tracking-wide">Contracted LOE</span>
                                    </td>
                                    {projectRows.map((row) => (
                                        <td key={row.id} className={`px-6 py-4 text-center border-r border-border font-semibold ${row.is_indirect ? 'bg-indigo-50' : ''}`}>
                                            {fmtLoe(row.contracted_loe)}
                                        </td>
                                    ))}
                                    <td className="px-6 py-4 border-r border-border" />
                                    <td className="px-6 py-4 border-r border-border" />
                                    <td className="sticky right-[100px] z-10 bg-gray-50 px-6 py-4 text-center border-l border-border font-bold">
                                        {fmtLoe(totalContractedLoe)}
                                    </td>
                                    <td className="sticky right-0 z-10 bg-gray-50 px-8 py-4 border-l border-border" />
                                </tr>

                                {/* Cumulative LOE row */}
                                <tr className="bg-gray-50 border-b-2 border-border text-gray-600">
                                    <td className="sticky left-0 z-10 bg-gray-50 px-6 py-4 border-r border-border" />
                                    <td className="sticky left-[36px] z-10 bg-gray-50 px-8 py-4 border-r border-border">
                                        <span className="font-semibold text-[10px] uppercase tracking-wide">Cumulative LOE % (to date)</span>
                                    </td>
                                    {projectRows.map((row) => (
                                        <td key={row.id} className={`px-4 py-2 border-r border-border ${row.is_indirect ? 'bg-indigo-50' : ''}`}>
                                            <input
                                                type="number"
                                                step="0.1" min="0" max="100"
                                                disabled={isLocked}
                                                defaultValue={row.cumulative_loe != null ? (parseFloat(row.cumulative_loe) * 100).toFixed(1) : ''}
                                                onBlur={(e) => handleCumulativeBlur(row.id, e.target.value)}
                                                className="w-full text-center text-xs outline-none bg-transparent border-b border-dashed border-gray-300 focus:border-org-blue-primary disabled:cursor-not-allowed disabled:opacity-50"
                                                placeholder="-"
                                            />
                                        </td>
                                    ))}
                                    <td className="px-6 py-4 border-r border-border" />
                                    <td className="px-6 py-4 border-r border-border" />
                                    <td className="sticky right-[100px] z-10 bg-gray-50 px-6 py-4 border-l border-border" />
                                    <td className="sticky right-0 z-10 bg-gray-50 px-8 py-4 border-l border-border" />
                                </tr>
                            </thead>

                            <tbody>
                                {/* Section C: day rows */}
                                {visibleDays.map((day) => {
                                    // actual API fields: work_date, day_index, is_weekend, public_holiday
                                    const isWeekend  = day.is_weekend;
                                    const isHoliday  = day.public_holiday;
                                    const inputsDisabled = isLocked || isWeekend || isHoliday;
                                    const rowBg = isWeekend ? 'bg-gray-100' : isHoliday ? 'bg-amber-50' : '';

                                    const dayNum = day.day_index;
                                    const dayName = new Date(day.work_date + 'T00:00:00')
                                        .toLocaleDateString('en-GB', { weekday: 'short' });
                                    const rowTotal = projectRows.reduce((sum, row) => {
                                        const h = getEntryHours(row.id, day.day_index);
                                        return sum + (h ? parseFloat(h) : 0);
                                    }, 0);

                                    return (
                                        <tr key={day.work_date} className={`border-b border-border ${rowBg}`}>
                                            <td className={`sticky left-0 z-10 px-6 py-4 text-center text-gray-400 font-bold border-r border-border ${rowBg || 'bg-white'}`}>
                                                {dayNum}
                                            </td>
                                            <td className={`sticky left-[36px] z-10 px-8 py-4 whitespace-nowrap border-r border-border font-medium ${rowBg || 'bg-white'} ${isWeekend ? 'text-gray-400' : isHoliday ? 'text-amber-700' : 'text-gray-700'}`}>
                                                {dayName} {dayNum}
                                                {isHoliday && (
                                                    <span className="ml-4 text-[9px] bg-amber-200 text-amber-800 px-4 py-1 rounded font-bold">PH</span>
                                                )}
                                                {isWeekend && <span className="ml-4 text-[9px] bg-gray-200 text-gray-500 px-4 py-1 rounded">WE</span>}
                                            </td>
                                            {projectRows.map((row) => (
                                                <td key={row.id} className={`px-4 py-2 border-r border-border ${row.is_indirect ? 'bg-indigo-50/30' : ''} ${rowBg}`}>
                                                    <input
                                                        type="number"
                                                        step="0.5" min="0"
                                                        disabled={inputsDisabled}
                                                        defaultValue={(() => {
                                                            const h = getEntryHours(row.id, day.day_index);
                                                            return h ? parseFloat(h).toFixed(2) : '';
                                                        })()}
                                                        onChange={(e) => handleCellChange(row.id, day.day_index, e.target.value)}
                                                        placeholder={inputsDisabled ? '' : '0.00'}
                                                        className={`w-full text-center text-xs outline-none bg-transparent focus:bg-org-blue-primary/10 rounded ${inputsDisabled ? 'cursor-not-allowed opacity-30' : ''}`}
                                                    />
                                                </td>
                                            ))}
                                            {/* Leave code */}
                                            <td className={`px-4 py-2 border-r border-border ${rowBg}`}>
                                                <select
                                                    disabled={isLocked || isWeekend}
                                                    value={day.leave_code || ''}
                                                    onChange={(e) => handleDayMeta(day.day_index, 'leave_code', e.target.value || null)}
                                                    className="w-full text-xs outline-none bg-transparent border-b border-dashed border-gray-300 focus:border-org-blue-primary disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    <option value="">—</option>
                                                    {LEAVE_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </td>
                                            {/* Public holiday checkbox */}
                                            <td className={`px-6 py-4 text-center border-r border-border ${rowBg}`}>
                                                <input
                                                    type="checkbox"
                                                    disabled={isLocked}
                                                    checked={!!day.public_holiday}
                                                    onChange={(e) => handleDayMeta(day.day_index, 'public_holiday', e.target.checked)}
                                                    className="cursor-pointer disabled:cursor-not-allowed"
                                                />
                                            </td>
                                            {/* Row total */}
                                            <td className={`sticky right-[100px] z-10 px-6 py-4 text-center font-bold border-l border-border ${rowBg || 'bg-gray-50'} ${rowTotal > 0 ? 'text-org-blue-primary' : 'text-gray-300'}`}>
                                                {rowTotal > 0 ? rowTotal.toFixed(2) : '—'}
                                            </td>
                                            {/* Activity note */}
                                            <td className={`sticky right-0 z-10 px-4 py-2 border-l border-border ${rowBg || 'bg-white'}`}>
                                                <input
                                                    type="text"
                                                    disabled={isLocked || isWeekend}
                                                    defaultValue={day.note || ''}
                                                    onBlur={(e) => e.target.value !== (day.note || '') && handleDayMeta(day.day_index, 'note', e.target.value)}
                                                    placeholder={inputsDisabled ? '' : 'Optional note…'}
                                                    className="w-full text-xs outline-none bg-transparent border-b border-dashed border-gray-300 focus:border-org-blue-primary disabled:cursor-not-allowed disabled:opacity-40 min-w-[90px]"
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}

                                {/* Month totals row */}
                                <tr className="bg-org-blue-primary/10 border-t-2 border-org-blue-primary font-bold">
                                    <td className="sticky left-0 z-10 bg-org-blue-primary/10 px-6 py-6 border-r border-border" />
                                    <td className="sticky left-[36px] z-10 bg-org-blue-primary/10 px-8 py-6 border-r border-border text-gray-700 text-[10px] uppercase tracking-wide whitespace-nowrap">
                                        Month Totals
                                    </td>
                                    {projectRows.map((row) => (
                                        <td key={row.id} className={`px-6 py-6 text-center border-r border-border text-org-blue-primary ${row.is_indirect ? 'bg-indigo-100/40' : ''}`}>
                                            {fmtHours(sheet?.month_totals?.per_column?.[row.col_ref] ?? getLineTotalHours(row)) || '0.00'}
                                        </td>
                                    ))}
                                    <td className="px-6 py-6 border-r border-border" />
                                    <td className="px-6 py-6 border-r border-border" />
                                    <td className="sticky right-[100px] z-10 bg-org-blue-primary/20 px-6 py-6 text-center border-l border-border text-org-blue-primary">
                                        {fmtHours(grandTotalHours) || '0.00'}
                                    </td>
                                    <td className="sticky right-0 z-10 bg-org-blue-primary/10 px-8 py-6 border-l border-border text-[9px] text-gray-400 font-normal">
                                        Leave: AL=Annual SL=Sick ML=Maternity CPD=Training COMP=Comp PH=Public Hol.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── SECTION D — LOE Compliance Summary ── */}
            {sheet && (
                <div className="bg-white rounded border border-border shadow-sm overflow-hidden">
                    <div className="px-15 py-10 border-b border-border bg-org-blue-primary">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white">
                            Section D — LOE Compliance Summary
                        </p>
                    </div>

                    {projectRows.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="border-collapse text-xs w-full min-w-max">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-border">
                                        <th className="px-10 py-8 text-left text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border min-w-[180px]">Project / Engagement</th>
                                        <th className="px-10 py-8 text-center text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border">Contracted LOE %</th>
                                        <th className="px-10 py-8 text-center text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border">Actual Hrs</th>
                                        <th className="px-10 py-8 text-center text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border">Actual LOE %</th>
                                        <th className="px-10 py-8 text-center text-[10px] font-bold uppercase tracking-wide text-gray-500 border-r border-border">Cumulative LOE % (to date)</th>
                                        <th className="px-10 py-8 text-center text-[10px] font-bold uppercase tracking-wide text-gray-500">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loeSummary.map((loeRow, i) => {
                                        const cumLoe = loeRow.cumulative_loe ?? projectRows.find(r => r.col_ref === loeRow.col_ref)?.cumulative_loe;
                                        return (
                                            <tr key={loeRow.col_ref} className={`border-b border-border ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                                <td className="px-10 py-8 border-r border-border">{loeRow.engagement_name}</td>
                                                <td className="px-10 py-8 text-center border-r border-border">{fmtLoe(loeRow.contracted_loe)}</td>
                                                <td className="px-10 py-8 text-center border-r border-border font-semibold">{(loeRow.actual_hours ?? 0).toFixed(2)}</td>
                                                <td className="px-10 py-8 text-center border-r border-border font-semibold">{fmtLoe(loeRow.actual_loe)}</td>
                                                <td className="px-10 py-8 text-center border-r border-border">
                                                    <div className="inline-flex items-center gap-4 border border-border rounded-md px-8 py-4 bg-gray-50 focus-within:border-org-blue-primary focus-within:bg-white transition-colors">
                                                        <input
                                                            key={cumLoe}
                                                            type="number"
                                                            step="0.1" min="0" max="100"
                                                            disabled={isLocked}
                                                            defaultValue={cumLoe != null ? (parseFloat(cumLoe) * 100).toFixed(1) : ''}
                                                            onBlur={(e) => {
                                                                const row = projectRows.find(r => r.col_ref === loeRow.col_ref);
                                                                if (row && e.target.value) handleCumulativeBlur(row.id, e.target.value);
                                                            }}
                                                            placeholder="0.0"
                                                            className="w-12 text-center text-xs outline-none bg-transparent text-gray-800 disabled:cursor-not-allowed"
                                                        />
                                                        <span className="text-[10px] text-gray-400 font-medium">%</span>
                                                    </div>
                                                </td>
                                                <td className="px-10 py-8 text-center">
                                                    <span className={`px-6 py-2 rounded-full text-[10px] font-semibold ${loeStatusPill[loeRow.status] || loeStatusPill['-']}`}>
                                                        {loeRow.status || '—'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    <tr className="bg-org-blue-primary/5 border-t-2 border-org-blue-primary font-bold">
                                        <td className="px-10 py-8 text-right text-[10px] uppercase tracking-wide text-gray-600">Total</td>
                                        <td className="px-10 py-8 text-center border-r border-border">{fmtLoe(totalContractedLoe)}</td>
                                        <td className="px-10 py-8 text-center border-r border-border text-org-blue-primary">{grandTotalHours.toFixed(2)}</td>
                                        <td className="px-10 py-8 text-center border-r border-border">{grandTotalHours > 0 ? '100.0%' : '—'}</td>
                                        <td colSpan={2} className="px-10 py-8 text-xs text-gray-400 font-normal">
                                            Any variance &gt;5% from contracted LOE requires written explanation below.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-20 text-sm text-gray-400 text-center">No project data to summarise.</div>
                    )}

                    {/* Variance explanation */}
                    <div className="p-15 border-t border-border">
                        <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wide block mb-8">
                            Explanation for unallocated hours or LOE variance &gt;5% from contracted level
                            {loeSummary.some((l) => ['Under target', 'Over target'].includes(l.status)) && (
                                <span className="ml-8 text-amber-600 font-normal">(Required)</span>
                            )}
                        </label>
                        <textarea
                            rows={3}
                            disabled={isLocked}
                            value={varianceText}
                            onChange={(e) => handleVarianceChange(e.target.value)}
                            placeholder="Explain any LOE variance greater than ±5 percentage points or any unallocated hours…"
                            className="w-full border border-border rounded-lg p-12 text-sm resize-none focus:outline-none focus:border-org-blue-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                    </div>
                </div>
            )}

            {/* ── SECTION E — Approval Chain ── */}
            {sheet && (
                <div className="bg-white rounded border border-border shadow-sm overflow-hidden">
                    <div className="px-15 py-10 border-b border-border bg-org-blue-primary">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white">
                            Section E — Approval Chain
                        </p>
                    </div>
                    <div className="p-20">
                        {/* Action buttons */}
                        <div className="flex flex-wrap gap-8 mb-20">
                            {canSubmit && (
                                <button
                                    onClick={handleSubmit}
                                    disabled={errorIssues.length > 0 || submitApi.isPending}
                                    className="flex items-center gap-5 bg-org-blue-primary text-white px-12 py-6 rounded text-xs font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <IconSignature size={13} />
                                    {submitApi.isPending ? 'Submitting…' : 'Submit & Self-Certify'}
                                </button>
                            )}
                            {canApprove && (
                                <button onClick={handleApprove} disabled={approveApi.isPending}
                                    className="flex items-center gap-5 bg-indigo-600 text-white px-12 py-6 rounded text-xs font-semibold hover:opacity-90 disabled:opacity-50">
                                    <IconCheck size={13} />
                                    {approveApi.isPending ? 'Approving…' : 'Line Manager Approve'}
                                </button>
                            )}
                            {canHrApprove && (
                                <button onClick={handleHrApprove} disabled={hrApproveApi.isPending}
                                    className="flex items-center gap-5 bg-green-600 text-white px-12 py-6 rounded text-xs font-semibold hover:opacity-90 disabled:opacity-50">
                                    <IconCheck size={13} />
                                    {hrApproveApi.isPending ? 'Approving…' : 'HR Manager Approve'}
                                </button>
                            )}
                            {canReject && (
                                <button onClick={() => setRejectModal({ open: true, reason: '' })}
                                    className="flex items-center gap-5 bg-red-50 text-red-600 border border-red-200 px-12 py-6 rounded text-xs font-semibold hover:bg-red-100">
                                    <IconX size={13} /> Reject
                                </button>
                            )}
                        </div>

                        {/* 3-step approval trail: Staff → Line Manager → HR Manager */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
                            {[
                                {
                                    step: 'Step 1',
                                    label: 'Staff Self-Certification',
                                    desc: 'Due: 1st working day of following month',
                                    ts: sheet.submitted_at,
                                    by: sheet.employee?.name,
                                    color: 'text-blue-700 bg-blue-50 border-blue-200',
                                },
                                {
                                    step: 'Step 2',
                                    label: 'Line Manager',
                                    desc: 'Verifies daily hours and activity notes',
                                    ts: sheet.approved_at,
                                    by: sheet.approved_by || sheet.line_manager?.name,
                                    color: 'text-indigo-700 bg-indigo-50 border-indigo-200',
                                },
                                {
                                    step: 'Step 3',
                                    label: 'HR Manager',
                                    desc: 'LOE reconciliation and payroll release',
                                    ts: sheet.hr_approved_at,
                                    by: sheet.hr_approved_by,
                                    color: 'text-green-700 bg-green-50 border-green-200',
                                },
                            ].map(({ step, label, desc, ts, by, color }) => (
                                <div key={step} className={`p-12 rounded border text-xs ${ts ? color : 'text-gray-400 bg-gray-50 border-gray-200'}`}>
                                    <p className="text-[9px] font-bold uppercase tracking-wide mb-4 opacity-60">{step}</p>
                                    <p className="font-semibold mb-3">{label}</p>
                                    {ts ? (
                                        <>
                                            <p className="flex items-center gap-4 mb-2">
                                                <IconClockHour4 size={11} />
                                                {new Date(ts).toLocaleString()}
                                            </p>
                                            {by && <p className="opacity-70">by {by}</p>}
                                        </>
                                    ) : (
                                        <>
                                            <p className="mb-2">Pending</p>
                                            <p className="opacity-50 text-[10px]">{desc}</p>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Staff certification text */}
                        <div className="mt-20 pt-15 border-t border-border">
                            <p className="text-[10px] text-gray-400 leading-relaxed italic">
                                Staff certification: I certify that the hours recorded above are a true and contemporaneous account of my effort distribution during this period,
                                that all project codes are accurate as registered with Finance, and that no hours have been double-charged across funding sources.
                                I understand that false certification constitutes a disciplinary offence and may constitute donor fraud.
                            </p>
                        </div>

                        {sheet.status === 'rejected' && sheet.rejected_at && (
                            <div className="mt-15 p-10 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                <strong>Rejected</strong> {new Date(sheet.rejected_at).toLocaleString()}
                                {sheet.rejection_reason && <span className="ml-8">— {sheet.rejection_reason}</span>}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── REJECT MODAL ── */}
            {rejectModal.open && (
                <>
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-20">
                        <div className="w-full max-w-[440px] bg-white rounded-xl shadow-2xl overflow-hidden">
                            <div className="bg-red-600 px-20 py-15 flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-white flex items-center gap-8">
                                    <IconX size={20} /> Reject Timesheet
                                </h3>
                                <button onClick={() => setRejectModal({ open: false, reason: '' })} className="p-4 hover:bg-white/20 rounded-full">
                                    <IconX size={20} className="text-white" />
                                </button>
                            </div>
                            <div className="p-20">
                                <label className="text-xs text-gray-500 font-semibold uppercase block mb-8">
                                    Rejection Reason <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows={4}
                                    value={rejectModal.reason}
                                    onChange={(e) => setRejectModal((s) => ({ ...s, reason: e.target.value }))}
                                    placeholder="Describe what needs to be corrected..."
                                    className="w-full border border-border rounded-lg p-12 text-sm resize-none focus:outline-none focus:border-red-400"
                                />
                            </div>
                            <div className="px-20 py-15 border-t border-border flex justify-end gap-10">
                                <button onClick={() => setRejectModal({ open: false, reason: '' })} className="btn btn-outline-secondary">
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReject}
                                    disabled={rejectApi.isPending || !rejectModal.reason.trim()}
                                    className="bg-red-600 text-white px-16 py-8 rounded text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                                >
                                    {rejectApi.isPending ? 'Rejecting…' : 'Confirm Reject'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div onClick={() => setRejectModal({ open: false, reason: '' })} className="fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm" />
                </>
            )}
        </div>
    );
}
